import { useSwrMutationApi } from '@hooks/useSwrApi';
import { useMemo } from 'react';

interface SaveInterviewQuestionsPayload {
  questions: string[];
}

interface UseSwrSaveInterviewQuestionsProps {
  locale?: string;
  jobSlug: string;
  onSuccess?: (response: unknown) => void;
  onError?: (error: unknown) => void;
}

/**
 * @description SWR mutation: save interview questions for a job
 *
 * @example
 * const { trigger, isMutating } = useSwrSaveInterviewQuestions({
 *   locale: 'ro',
 *   jobSlug: 'software-engineer-123',
 *   onSuccess: () => console.log('Questions saved'),
 *   onError: (error) => console.error('Failed to save questions')
 * });
 *
 * // Trigger the save
 * void trigger({ questions: ['Question 1', 'Question 2', 'Question 3'] });
 */
export const useSwrSaveInterviewQuestions = (props: UseSwrSaveInterviewQuestionsProps) => {
  const {
    locale = 'ro', jobSlug, onSuccess, onError,
  } = props;

  // Build the URL with path parameters
  const url = useMemo(
    () => `/v1/interview-questions/${jobSlug}/save`,
    [jobSlug],
  );

  const {
    data,
    error,
    trigger,
    isMutating,
    reset,
  } = useSwrMutationApi<unknown, SaveInterviewQuestionsPayload>({
    url,
    locale,
    fetcherConfig: {
      method: 'POST',
      stringifyPayload: true,
      headers: {
        'Content-Type': 'application/json',
      },
    },
    apiOptions: {
      onSuccess: (response) => {
        if (onSuccess) {
          onSuccess(response);
        }
      },
      onError: (err) => {
        if (onError) {
          onError(err);
        }
      },
    },
    toastsOptions: {
      showErrorToast: true,
    },
  });

  return {
    data,
    error,
    trigger,
    isMutating,
    reset,
  };
};
